/**
 * Smart Stable — Premium XLSX sheet builder (ExcelJS).
 *
 * Drop-in helpers that produce a uniformly-styled workbook across every
 * export in the project. The look matches the project's gold identity
 * (banner #B79B5B + cream-soft alt rows + thin borders + currency-formatted
 * money columns + freeze panes + autofilter + A4 landscape print setup).
 *
 * Migrated from xlsx-js-style to ExcelJS (removes the SheetJS prototype-
 * pollution CVE; ExcelJS ships native free styling and a browser bundle).
 *
 * Usage (one-shot — most common):
 *     import { exportStyledWorkbook } from '../../utils/xlsxStyled';
 *     await exportStyledWorkbook({ fileName, sheetName: 'Horse list', rows, subtitle });
 *
 * Usage (multi-sheet):
 *     import { createStyledWorkbook, appendStyledSheet, saveStyledWorkbook } from '../../utils/xlsxStyled';
 *     const wb = createStyledWorkbook();
 *     appendStyledSheet(wb, 'Sheet A', rowsA);
 *     appendStyledSheet(wb, 'Sheet B', rowsB);
 *     await saveStyledWorkbook(wb, fileName);
 */

import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// ---------------------------------------------------------------------------
// Palette — mirrors smartStableTheme.js (hex without the leading #)
// ---------------------------------------------------------------------------
export const XLSX_PALETTE = {
  gold:       'B79B5B',
  goldSoft:   'EFE4CB',
  cardSoft:   'F7F3EA',
  line:       'EADFCA',
  text:       '4F545C',
  muted:      '7A7F87',
  greenSoft:  'EDF5EE',
  roseSoft:   'F6ECEA',
};

// ExcelJS colours are ARGB ('FF' alpha + RGB hex).
const argb = (rgb) => `FF${String(rgb).replace(/^#/, '')}`;

// Header substrings that should be auto-formatted as money (`#,##0.00`).
// Covers EN + AR — extend here if a new vocabulary appears.
const MONEY_KEYWORDS = [
  'amount', 'price', 'revenue', 'expense', 'income', 'total', 'fee', 'cost',
  'prize', 'paid', 'received', 'sar', 'currency', 'balance', 'subtotal',
  'مبلغ', 'سعر', 'إيراد', 'مصروف', 'دخل', 'إجمالي', 'رسوم', 'تكلفة', 'جائزة',
  'مدفوع', 'مستلم', 'رصيد',
];

export function isMoneyHeader(header) {
  const h = String(header || '').toLowerCase();
  return MONEY_KEYWORDS.some((kw) => h.includes(kw));
}

// رقم عمود (1-based) إلى حرفه (1→A، 27→AA) — لبناء نطاقات الدمج.
function colLetter(n) {
  let s = '';
  let num = n;
  while (num > 0) {
    const rem = (num - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    num = Math.floor((num - 1) / 26);
  }
  return s;
}

// ينشئ مصنّف ExcelJS فارغًا (بديل XLSX.utils.book_new للحالات متعددة الأوراق).
export function createStyledWorkbook() {
  return new ExcelJS.Workbook();
}

// ---------------------------------------------------------------------------
// Append a fully-styled sheet to an existing ExcelJS workbook.
//
//   workbook    — workbook created via createStyledWorkbook()
//   sheetName   — appears in the gold banner AND as the tab title
//   rows        — array of objects; keys become column headers
//   subtitle    — optional cream-row beneath the banner
//   options     — { palette, footerNote }
// ---------------------------------------------------------------------------
export function appendStyledSheet(workbook, sheetName, rows, subtitle = '', options = {}) {
  const palette = options.palette || XLSX_PALETTE;
  const headers = rows.length ? Object.keys(rows[0]) : ['-'];
  const safeRows = rows.length ? rows : [{ '-': '-' }];
  const COLS = headers.length;
  const today = new Date().toISOString().slice(0, 10);
  const generatedAt = options.footerNote || `Generated at: ${new Date().toLocaleString()}`;

  const ws = workbook.addWorksheet(String(sheetName).slice(0, 31), {
    views: [{ state: 'frozen', ySplit: 3 }], // تجميد البانر/العنوان/الرؤوس
    pageSetup: { paperSize: 9, orientation: 'landscape', fitToWidth: 1, fitToHeight: 0 },
  });

  const thin = { style: 'thin', color: { argb: argb(palette.line) } };
  const border = { top: thin, bottom: thin, left: thin, right: thin };
  const fill = (rgb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb: argb(rgb) } });
  const center = (wrap = true) => ({ horizontal: 'center', vertical: 'middle', wrapText: wrap });

  // Layout: banner(1) + subtitle(2) + header(3) + data + blank + footer
  ws.addRow([sheetName]);                                   // row 1
  ws.addRow([subtitle || `${sheetName} · ${today}`]);      // row 2
  ws.addRow(headers);                                       // row 3
  safeRows.forEach((row) => {
    ws.addRow(
      headers.map((h) => {
        const raw = row[h];
        if (raw === null || raw === undefined || raw === '') return '';
        if (isMoneyHeader(h)) {
          const n = Number(String(raw).replace(/[^\d.-]/g, ''));
          if (Number.isFinite(n)) return n;
        }
        return raw;
      })
    );
  });
  ws.addRow([]);                                            // blank
  const footerRow = ws.addRow([generatedAt]);               // footer
  const lastRowNum = footerRow.number;
  const lastCol = colLetter(COLS);

  // Merges: banner / subtitle / footer span the full width (only if >1 col).
  if (COLS > 1) {
    ws.mergeCells(`A1:${lastCol}1`);
    ws.mergeCells(`A2:${lastCol}2`);
    ws.mergeCells(`A${lastRowNum}:${lastCol}${lastRowNum}`);
  }

  // Banner (row 1)
  ws.getRow(1).height = 30;
  const bannerCell = ws.getCell('A1');
  bannerCell.fill = fill(palette.gold);
  bannerCell.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
  bannerCell.alignment = center();

  // Subtitle (row 2)
  ws.getRow(2).height = 22;
  const subCell = ws.getCell('A2');
  subCell.fill = fill(palette.cardSoft);
  subCell.font = { bold: true, size: 11, color: { argb: argb(palette.text) } };
  subCell.alignment = center();
  subCell.border = border;

  // Header (row 3)
  const headerRow = ws.getRow(3);
  headerRow.height = 20;
  for (let c = 1; c <= COLS; c += 1) {
    const cell = headerRow.getCell(c);
    cell.fill = fill(palette.gold);
    cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
    cell.alignment = center();
    cell.border = border;
  }

  // Data rows (from row 4)
  const dataStartRow = 4;
  for (let i = 0; i < safeRows.length; i += 1) {
    const r = ws.getRow(dataStartRow + i);
    r.height = 20;
    const alt = i % 2 === 1;
    for (let c = 1; c <= COLS; c += 1) {
      const moneyCol = isMoneyHeader(headers[c - 1]);
      const cell = r.getCell(c);
      cell.alignment = center(!moneyCol);
      cell.font = { size: 11, bold: moneyCol, color: { argb: argb(palette.text) } };
      cell.border = border;
      if (alt) cell.fill = fill(palette.cardSoft);
      if (moneyCol) cell.numFmt = '#,##0.00';
    }
  }

  // Footer
  const footerCell = ws.getCell(`A${lastRowNum}`);
  footerCell.font = { italic: true, size: 9, color: { argb: argb(palette.muted) } };
  footerCell.alignment = { horizontal: 'center', vertical: 'middle' };

  // Column widths — heuristic per header length + content length
  headers.forEach((h, idx) => {
    const headerLen = String(h).length;
    let contentMax = 0;
    safeRows.forEach((row) => {
      const v = row[h];
      const len = v == null ? 0 : String(v).length;
      if (len > contentMax) contentMax = len;
    });
    const base = Math.max(headerLen + 4, contentMax + 2);
    const cap = isMoneyHeader(h) ? 22 : 38;
    ws.getColumn(idx + 1).width = Math.min(Math.max(base, isMoneyHeader(h) ? 14 : 12), cap);
  });

  // Auto-filter on the data range (header row 3 .. last data row)
  if (rows.length) {
    ws.autoFilter = { from: { row: 3, column: 1 }, to: { row: 3 + rows.length, column: COLS } };
  }

  return ws;
}

// ---------------------------------------------------------------------------
// One-shot helper — creates a workbook, appends ONE styled sheet, saves it.
// Use this when a page exports a single sheet (most common case).
// Returns a Promise (ExcelJS writes asynchronously).
// ---------------------------------------------------------------------------
export function exportStyledWorkbook({
  fileName,
  sheetName,
  rows,
  subtitle = '',
  palette,
  footerNote,
}) {
  const wb = createStyledWorkbook();
  appendStyledSheet(wb, sheetName, rows, subtitle, { palette, footerNote });
  return saveStyledWorkbook(wb, fileName);
}

// ---------------------------------------------------------------------------
// Write + download an already-built workbook with the styles preserved.
// ---------------------------------------------------------------------------
export async function saveStyledWorkbook(workbook, fileName) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, fileName);
  return true;
}

const xlsxStyled = {
  createStyledWorkbook,
  appendStyledSheet,
  exportStyledWorkbook,
  saveStyledWorkbook,
  isMoneyHeader,
  XLSX_PALETTE,
};

export default xlsxStyled;
